import React from "react";
import { useTranslation } from "@/lib/i18n";
import { fmtPercent } from "@/components/portfolio/_format";
import InfoTooltip from "@/components/portfolio/InfoTooltip";

function InsightCard({ id, accent, label, ticker, valueText, hint, testId, tooltipText, tooltipTestId }) {
  const accentStyles = {
    cyan: { color: "#3DEBFF", glow: "rgba(61,235,255,0.45)" },
    green: { color: "#2BFF8A", glow: "rgba(43,255,138,0.45)" },
    indigo: { color: "#A78BFA", glow: "rgba(167,139,250,0.4)" },
    loss: { color: "#FF3DA6", glow: "rgba(255,61,166,0.4)" },
  };
  const a = accentStyles[accent] || accentStyles.cyan;
  return (
    <div
      data-testid={testId}
      className="relative overflow-hidden rounded-xl bg-card/60 backdrop-blur-xl border border-white/10 p-4 min-w-[220px] flex-1"
    >
      <div className="absolute top-0 left-4 right-4 h-[2px] rounded-full" style={{ background: a.color, boxShadow: `0 0 12px ${a.glow}` }} />
      <div className="flex items-center gap-1.5 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground truncate">{label}</div>
        {tooltipText ? (
          <InfoTooltip text={tooltipText} testId={tooltipTestId} label={label} />
        ) : null}
      </div>
      {ticker ? (
        <div className="mt-2 flex items-baseline gap-2">
          <span className="font-mono text-lg font-medium">{ticker}</span>
          {valueText ? <span className="tabular text-sm" style={{ color: a.color }}>{valueText}</span> : null}
        </div>
      ) : (
        <div className="mt-2 text-sm text-muted-foreground">—</div>
      )}
      {hint ? <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{hint}</div> : null}
    </div>
  );
}

export default function PortfolioInsightCards({ insights }) {
  const { t } = useTranslation();
  const i = insights || {};
  const concentration = i.concentration_pct;

  const cards = [
    {
      id: "top-opportunity",
      label: t("portfolio.insights.top_opportunity"),
      data: i.top_opportunity,
      accent: "green",
      hint: t("portfolio.insights.top_opportunity_hint"),
      tooltip: t("portfolio.tooltip.top_opportunity"),
      fmt: (v) => v !== null && v !== undefined ? `${v >= 0 ? "+" : ""}${fmtPercent(v)}` : null,
    },
    {
      id: "largest-risk",
      label: t("portfolio.insights.largest_risk"),
      data: i.largest_valuation_risk,
      accent: "loss",
      hint: t("portfolio.insights.largest_risk_hint"),
      tooltip: t("portfolio.tooltip.largest_valuation_risk"),
      fmt: (v) => v !== null && v !== undefined ? fmtPercent(v) : null,
    },
    {
      id: "highest-weight",
      label: t("portfolio.insights.highest_weight"),
      data: i.highest_weight,
      accent: "indigo",
      hint: t("portfolio.insights.highest_weight_hint"),
      tooltip: t("portfolio.tooltip.position_weight"),
      fmt: (v) => v !== null && v !== undefined ? fmtPercent(v) : null,
    },
    {
      id: "biggest-winner",
      label: t("portfolio.insights.biggest_winner"),
      data: i.biggest_winner,
      accent: "cyan",
      hint: t("portfolio.insights.biggest_winner_hint"),
      tooltip: t("portfolio.insights.biggest_winner_hint"),
      fmt: (v) => v !== null && v !== undefined ? `${v >= 0 ? "+" : ""}${fmtPercent(v)}` : null,
    },
    {
      id: "biggest-loser",
      label: t("portfolio.insights.biggest_loser"),
      data: i.biggest_loser,
      accent: "loss",
      hint: t("portfolio.insights.biggest_loser_hint"),
      tooltip: t("portfolio.insights.biggest_loser_hint"),
      fmt: (v) => v !== null && v !== undefined ? fmtPercent(v) : null,
    },
    {
      id: "concentration",
      label: t("portfolio.insights.concentration"),
      data: concentration !== null && concentration !== undefined ? { ticker: i.highest_weight?.ticker, value: concentration } : null,
      accent: "cyan",
      hint: (concentration || 0) > 0.35 ? t("portfolio.insights.concentration_high") : t("portfolio.insights.concentration_normal"),
      tooltip: t("portfolio.tooltip.concentration"),
      fmt: (v) => v !== null && v !== undefined ? fmtPercent(v) : null,
    },
  ];

  return (
    <div
      data-testid="portfolio-insights-row"
      className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3"
    >
      {cards.map((c) => (
        <InsightCard
          key={c.id}
          id={c.id}
          accent={c.accent}
          label={c.label}
          ticker={c.data?.ticker}
          valueText={c.fmt(c.data?.value)}
          hint={c.hint}
          testId={`portfolio-insight-card-${c.id}`}
          tooltipText={c.tooltip}
          tooltipTestId={`portfolio-insight-${c.id}-tooltip`}
        />
      ))}
    </div>
  );
}
